import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Flex,
  Spacer,
  Button,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardHeader,
  CardBody,
  Grid,
  GridItem,
  Progress,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import { SearchIcon, ViewIcon } from "@chakra-ui/icons";
import { getCancellationFeedback, CancellationFeedbackFilters } from "../../../api/Admin";

interface CancellationData {
  cancellationId: number;
  userId: number;
  userEmail: string | null;
  stripeSubscriptionId: string | null;
  primaryReason: string;
  subReason: string | null;
  usageFrequency: string | null;
  satisfactionRatings: string | null;
  missingFeatures: string[] | null;
  alternativeService: string | null;
  detailedFeedback: string;
  allowFollowUp: boolean;
  stripeCancelledAt: string | null;
  createdAt: string;
}

interface FeedbackSummary {
  totalCount: number;
  reasonBreakdown: { [key: string]: number };
  averageRatings: { [key: string]: { average: number; count: number } };
  followUpAvailable: number;
  followUpPercentage: string;
}

const FeedbackAdmin = () => {
  const [cancellations, setCancellations] = useState<CancellationData[]>([]);
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<CancellationFeedbackFilters>({
    pageSize: 50,
  });
  const [selectedCancellation, setSelectedCancellation] = useState<CancellationData | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const loadFeedback = async () => {
    setLoading(true);
    try {
      const response = await getCancellationFeedback(filters);
      if (response.success) {
        setCancellations(response.data.cancellations);
        setSummary(response.data.summary);
      } else {
        toast({
          title: "Error loading feedback",
          description: response.message || "Failed to load cancellation feedback",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load cancellation feedback",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedback();
  }, [filters]);

  const handleFilterChange = (key: keyof CancellationFeedbackFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString() + " " + new Date(dateString).toLocaleTimeString();
  };

  const getParsedRatings = (ratingsString: string | null) => {
    if (!ratingsString) return null;
    try {
      return JSON.parse(ratingsString);
    } catch {
      return null;
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return "green";
    if (rating >= 3) return "yellow";
    return "red";
  };

  const getReasonColor = (reason: string) => {
    const colors: { [key: string]: string } = {
      "pricing": "red",
      "features": "blue", 
      "switching": "orange",
      "technical": "purple",
      "usage": "gray",
      "other": "teal"
    };
    return colors[reason] || "gray";
  };

  const viewDetails = (cancellation: CancellationData) => {
    setSelectedCancellation(cancellation);
    onOpen();
  };

  if (loading) {
    return (
      <Box p={6}>
        <Text>Loading cancellation feedback...</Text>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading size="lg" mb={2}>Subscription Cancellation Feedback</Heading>
          <Text color="gray.600">
            Analyze user feedback and cancellation patterns to improve retention
          </Text>
        </Box>

        <Tabs>
          <TabList>
            <Tab>📊 Overview</Tab>
            <Tab>📋 Feedback List</Tab>
          </TabList>

          <TabPanels>
            {/* Overview Tab */}
            <TabPanel>
              {summary && (
                <Grid templateColumns="repeat(auto-fit, minmax(250px, 1fr))" gap={6}>
                  <Card>
                    <CardHeader>
                      <Heading size="md">Summary Statistics</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={4} align="stretch">
                        <Stat>
                          <StatLabel>Total Cancellations</StatLabel>
                          <StatNumber>{summary.totalCount}</StatNumber>
                        </Stat>
                        <Stat>
                          <StatLabel>Follow-up Available</StatLabel>
                          <StatNumber>{summary.followUpAvailable}</StatNumber>
                          <StatHelpText>{summary.followUpPercentage}% of total</StatHelpText>
                        </Stat>
                      </VStack>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardHeader>
                      <Heading size="md">Cancellation Reasons</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={3} align="stretch">
                        {Object.entries(summary.reasonBreakdown).map(([reason, count]) => (
                          <Box key={reason}>
                            <Flex justify="space-between" align="center" mb={1}>
                              <Badge colorScheme={getReasonColor(reason)} size="sm">
                                {reason}
                              </Badge>
                              <Text fontSize="sm" fontWeight="bold">{count}</Text>
                            </Flex>
                            <Progress 
                              value={(count / summary.totalCount) * 100} 
                              colorScheme={getReasonColor(reason)}
                              size="sm"
                            />
                          </Box>
                        ))}
                      </VStack>
                    </CardBody>
                  </Card>

                  <Card>
                    <CardHeader>
                      <Heading size="md">Average Satisfaction Ratings</Heading>
                    </CardHeader>
                    <CardBody>
                      <VStack spacing={3} align="stretch">
                        {Object.entries(summary.averageRatings).map(([category, data]) => (
                          <Box key={category}>
                            <Flex justify="space-between" align="center" mb={1}>
                              <Text fontSize="sm" textTransform="capitalize">{category}</Text>
                              <Badge colorScheme={getRatingColor(data.average)} size="sm">
                                {data.average.toFixed(1)}/5
                              </Badge>
                            </Flex>
                            <Progress 
                              value={(data.average / 5) * 100} 
                              colorScheme={getRatingColor(data.average)}
                              size="sm"
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              {data.count} responses
                            </Text>
                          </Box>
                        ))}
                      </VStack>
                    </CardBody>
                  </Card>
                </Grid>
              )}
            </TabPanel>

            {/* Feedback List Tab */}
            <TabPanel>
              <VStack spacing={4} align="stretch">
                {/* Filters */}
                <Card>
                  <CardBody>
                    <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
                      <Box>
                        <Text fontSize="sm" mb={2}>Primary Reason</Text>
                        <Select 
                          placeholder="All reasons"
                          value={filters.primaryReason || ""}
                          onChange={(e) => handleFilterChange("primaryReason", e.target.value)}
                        >
                          <option value="pricing">Pricing</option>
                          <option value="features">Features</option>
                          <option value="switching">Switching</option>
                          <option value="technical">Technical</option>
                          <option value="usage">Usage</option>
                          <option value="other">Other</option>
                        </Select>
                      </Box>
                      <Box>
                        <Text fontSize="sm" mb={2}>Follow-up Consent</Text>
                        <Select 
                          placeholder="All"
                          value={filters.allowFollowUp?.toString() || ""}
                          onChange={(e) => handleFilterChange("allowFollowUp", e.target.value ? e.target.value === "true" : undefined)}
                        >
                          <option value="true">Allows follow-up</option>
                          <option value="false">No follow-up</option>
                        </Select>
                      </Box>
                      <Box>
                        <Text fontSize="sm" mb={2}>Date From</Text>
                        <Input 
                          type="date"
                          value={filters.createdAfter || ""}
                          onChange={(e) => handleFilterChange("createdAfter", e.target.value)}
                        />
                      </Box>
                      <Box>
                        <Text fontSize="sm" mb={2}>Date To</Text>
                        <Input 
                          type="date"
                          value={filters.createdBefore || ""}
                          onChange={(e) => handleFilterChange("createdBefore", e.target.value)}
                        />
                      </Box>
                    </Grid>
                  </CardBody>
                </Card>

                {/* Results Table */}
                <TableContainer>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Date</Th>
                        <Th>User Email</Th>
                        <Th>Primary Reason</Th>
                        <Th>Sub Reason</Th>
                        <Th>Usage Frequency</Th>
                        <Th>Follow-up</Th>
                        <Th>Stripe Status</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {cancellations.map((cancellation) => (
                        <Tr key={cancellation.cancellationId}>
                          <Td fontSize="sm">{formatDate(cancellation.createdAt)}</Td>
                          <Td fontSize="sm">{cancellation.userEmail || `User ID: ${cancellation.userId}`}</Td>
                          <Td>
                            <Badge colorScheme={getReasonColor(cancellation.primaryReason)} size="sm">
                              {cancellation.primaryReason}
                            </Badge>
                          </Td>
                          <Td fontSize="sm">{cancellation.subReason || "-"}</Td>
                          <Td fontSize="sm">{cancellation.usageFrequency || "-"}</Td>
                          <Td>
                            <Badge colorScheme={cancellation.allowFollowUp ? "green" : "gray"} size="sm">
                              {cancellation.allowFollowUp ? "Yes" : "No"}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge colorScheme={cancellation.stripeCancelledAt ? "red" : "gray"} size="sm">
                              {cancellation.stripeCancelledAt ? "Cancelled" : "N/A"}
                            </Badge>
                          </Td>
                          <Td>
                            <Button 
                              size="sm" 
                              leftIcon={<ViewIcon />}
                              onClick={() => viewDetails(cancellation)}
                            >
                              View
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>

                {cancellations.length === 0 && (
                  <Alert status="info">
                    <AlertIcon />
                    <AlertTitle>No feedback found</AlertTitle>
                    <AlertDescription>
                      No cancellation feedback matches the current filters.
                    </AlertDescription>
                  </Alert>
                )}
              </VStack>
            </TabPanel>

           
          </TabPanels>
        </Tabs>
      </VStack>

      {/* Detail Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Cancellation Feedback Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedCancellation && (
              <VStack spacing={6} align="stretch">
                <Grid templateColumns="repeat(2, 1fr)" gap={6}>
                  <Box>
                    <Text fontWeight="bold" mb={2}>User Information</Text>
                    <VStack spacing={2} align="start">
                      <Text fontSize="sm"><strong>Email:</strong> {selectedCancellation.userEmail || "Not available"}</Text>
                      <Text fontSize="sm"><strong>User ID:</strong> {selectedCancellation.userId}</Text>
                      <Text fontSize="sm"><strong>Date:</strong> {formatDate(selectedCancellation.createdAt)}</Text>
                      <Text fontSize="sm"><strong>Follow-up Consent:</strong> 
                        <Badge ml={2} colorScheme={selectedCancellation.allowFollowUp ? "green" : "gray"}>
                          {selectedCancellation.allowFollowUp ? "Yes" : "No"}
                        </Badge>
                      </Text>
                    </VStack>
                  </Box>
                  
                  <Box>
                    <Text fontWeight="bold" mb={2}>Cancellation Details</Text>
                    <VStack spacing={2} align="start">
                      <Text fontSize="sm"><strong>Primary Reason:</strong> 
                        <Badge ml={2} colorScheme={getReasonColor(selectedCancellation.primaryReason)}>
                          {selectedCancellation.primaryReason}
                        </Badge>
                      </Text>
                      <Text fontSize="sm"><strong>Sub Reason:</strong> {selectedCancellation.subReason || "N/A"}</Text>
                      <Text fontSize="sm"><strong>Usage Frequency:</strong> {selectedCancellation.usageFrequency || "N/A"}</Text>
                      <Text fontSize="sm"><strong>Alternative Service:</strong> {selectedCancellation.alternativeService || "N/A"}</Text>
                    </VStack>
                  </Box>
                </Grid>

                <Divider />

                {selectedCancellation.satisfactionRatings && (
                  <Box>
                    <Text fontWeight="bold" mb={3}>Satisfaction Ratings</Text>
                    <Grid templateColumns="repeat(auto-fit, minmax(150px, 1fr))" gap={4}>
                      {Object.entries(getParsedRatings(selectedCancellation.satisfactionRatings) || {}).map(([category, rating]) => (
                        <Box key={category} textAlign="center">
                          <Text fontSize="sm" textTransform="capitalize" mb={1}>{category}</Text>
                          <Badge colorScheme={getRatingColor(parseInt(rating as string))} size="lg">
                            {rating as string}/5
                          </Badge>
                        </Box>
                      ))}
                    </Grid>
                  </Box>
                )}

                {selectedCancellation.missingFeatures && selectedCancellation.missingFeatures.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={3}>Missing Features</Text>
                    <HStack spacing={2} wrap="wrap">
                      {selectedCancellation.missingFeatures.map((feature, index) => (
                        <Badge key={index} colorScheme="blue" variant="outline">
                          {feature}
                        </Badge>
                      ))}
                    </HStack>
                  </Box>
                )}

                <Box>
                  <Text fontWeight="bold" mb={3}>Detailed Feedback</Text>
                  <Textarea 
                    value={selectedCancellation.detailedFeedback}
                    isReadOnly
                    minHeight="120px"
                  />
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default FeedbackAdmin;